import React, { useEffect, useRef } from "react";
import Button from "@material-ui/core/Button";
import styled from "styled-components";
import { makeStyles } from "@material-ui/core/styles";
import { FileUploadS3 } from "../../../utils/FileUpload";
import { connect } from "react-redux";
import Plus from "sushiweb/Icons/all/Plus";
import { bindActionCreators } from "redux";
import * as userActions from "../../../store/actions/User";
import Errors from "../../../constants/Errors.json";
import SuccessMessages from "../../../constants/SucessMessages.json";
import PropTypes from "prop-types";
import commonStyles from "../../../static/styles/common";
import validateCSV from "../../../utils/CsvValidator";

const useStyles = makeStyles((_theme) => commonStyles.fileUploadButton);

const FileUpload = (props) => {
  const {
    title,
    presign_url,
    callback_key,
    uploadUrl,
    setStatus,
    handleClose,
    keyName,
    activeFile,
    setActiveFile,
    activeFileName,
    setActiveFileName,
    validateCSVSize,
    onError,
    disabled,
    acceptTypes,
    maxSize,
    UploadIcon,
    heading, //either send title or heading
    extraParamsAckObj, // extra params to send to ack call along with callback key
    handleFileUploadSuccess, //call this function when file upload is successful
    noAckCall,
    validationType, //for CSV validation of your choice
    maxNumberOfRows, // max number of rows allowed
    addTimestampToFileName,
  } = props;
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const validateFileSize = (fileSize, maxSize) => {
    // fileSize - in mb, maxSize in mb,
    // 1048576 bytes = 1 mb
    if (fileSize == 0) {
      setStatus({
        type: "error",
        message: "Empty file is not allowed",
      });
      return false;
    }
    if (fileSize > maxSize * 1048576) {
      setStatus({
        type: "error",
        message: "Please select file less than " + maxSize + " MB",
      });
      return false;
    }
    return true;
  };

  const validateNumberRows = (numberOfRows, maxNumberOfRows) => {
    if (numberOfRows === 0) {
      setStatus({
        type: "error",
        message: "Empty file is not allowed",
      });
      return false;
    }
    if (numberOfRows > maxNumberOfRows) {
      setStatus({
        type: "error",
        message: "Number of rows cannot exceed " + maxNumberOfRows,
      });
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    const files = e.target.files;
    if (!files) {
      setStatus({
        type: "error",
        message: "Something went wrong",
      });
    }
    if (setActiveFileName) {
      setActiveFileName(files[0].name);
      if (addTimestampToFileName) {
        const timestamp = new Date().getTime();
        setActiveFileName(`${timestamp}-${files[0].name}`);
      }
    }

    if (files[0].type == "text/csv") {
      if (validateCSVSize) {
        const valid = validateFileSize(files[0].size, maxSize);
        if (valid === false) {
          setActiveFileName("");
          setActiveFile("");
          fileInputRef.current.value = "";
          return;
        }
      }
      const reader = new FileReader();
      reader.onload = () => {
        const csvContent = reader.result;
        const lines = csvContent.split("\n").map((val) => val.trim());
        const valid = validateNumberRows(lines.length, maxNumberOfRows);
        if (valid == false) {
          fileInputRef.current.value = "";
          return;
        }
        if (validationType) {
          const { validationStatus, csvHeaders } = validateCSV(
            validationType,
            csvContent
          );
          if (validationStatus === "validated") {
            setActiveFile(files[0]);
            props.setActiveCsvHeaders(csvHeaders);
          } else {
            fileInputRef.current.value = "";
            handleClose("failure");
            setStatus({
              type: "error",
              message: validationStatus,
            });
          }
        }
        setActiveFile(files[0]);
      };
      reader.readAsText(files[0]);
    } else {
      const valid = validateFileSize(files[0].size, maxSize);
      if (valid == false) {
        fileInputRef.current.value = "";
        return;
      }
      setActiveFile(files[0]);
    }
  };

  useEffect(() => {
    if (presign_url && activeFile) {
      FileUploadS3(
        callback_key,
        presign_url,
        activeFile,
        uploadUrl,
        keyName,
        noAckCall,
        extraParamsAckObj
      )
        .then((url) => {
          if (typeof handleFileUploadSuccess === "function") {
            handleFileUploadSuccess();
          }
          handleClose("success");
          setStatus({
            type: "success",
            message: SuccessMessages.GENERAL_SUCCESS_MESSAGE,
          });
        })
        .catch((err) => {
          if (onError) {
            if (typeof onError == "function") {
              onError(err);
              return;
            }
          }
          handleClose("failure");
          setStatus({
            type: "error",
            message: typeof err === "string" ? err : Errors.FILE_UPLOAD_FAILED,
          });
        });
    } else if (callback_key && presign_url && !activeFile) {
      setStatus({ type: "error", message: Errors.NO_FILE_SELECTED });
    }
  }, [callback_key, presign_url]);

  useEffect(() => {
    if (activeFile) {
      fileInputRef.current.value = "";
    }
  }, [activeFile]);

  return (
    <FileUploadComponent className="file-upload-box">
      <Button
        icon={<Plus />}
        className={classes.uploadButton}
        variant="outlined"
        component="label"
        disabled={disabled}
      >
        {UploadIcon ? <span className="upload-icon">{UploadIcon}</span> : ""}
        {heading ? heading : `Upload ${title || "File"}`}
        <input
          // disabled={callback_key}
          ref={fileInputRef}
          type="file"
          hidden
          id="file-input"
          multiple
          onChange={handleChange}
          accept={acceptTypes ? acceptTypes : undefined}
        />
      </Button>
      {activeFile && (
        <p className={`${classes.fileName} mt-10`}>{activeFile.name}</p>
      )}
    </FileUploadComponent>
  );
};

const FileUploadComponent = styled.div`
  label {
    width: 100%;
  }
  .upload-icon {
    margin-right: 10px;
    margin-bottom: 5px;
  }
`;

FileUpload.defaultProps = {
  disabled: false,
  validationType: "",
  maxSize: 1.5, // in mb
  maxNumberOfRows: 100001,
  validateCSVSize: false,
};

FileUpload.propTypes = {
  title: PropTypes.string,
  presign_url: PropTypes.string.isRequired,
  callback_key: PropTypes.string.isRequired,
  uploadUrl: PropTypes.string.isRequired,
  keyName: PropTypes.string.isRequired,
  activeFile: PropTypes.object.isRequired,
  setActiveFile: PropTypes.func.isRequired,
  handleClose: PropTypes.func,
  setStatus: PropTypes.func,
  onError: PropTypes.func,
  acceptTypes: PropTypes.string,
  // maxsize is in Mega Bytes
  maxSize: PropTypes.number,
  validationType: PropTypes.string,
  maxNumberOfRows: PropTypes.number,
};

function mapStateToProps(state) {
  return {};
}

function mapDispatchToProps(dispatch) {
  return {
    setStatus: bindActionCreators(userActions.setStatus, dispatch),
    setActiveCsvHeaders: bindActionCreators(
      userActions.setActiveCsvHeaders,
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(FileUpload);
